import React, { useState } from 'react';
import { isMobileOnly } from 'react-device-detect';

import { Box, IconButton, Stack, Typography } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MenuIcon from '@mui/icons-material/Menu';
import AppLogo from '@components/AppLogo';
import DashboardBarButton from './DashboardBarButton';
import ParticipantsButton from './ParticipantsButton';
import FullScreenButton from './FullScreenButton';
import UserAvatarButton from '@components/UserAvatarButton';
import AdminTools from './AdminTools';
import { ReactComponent as LogoutIcon } from '@public/images/account_exit.svg';
import { env } from 'env';
import MytaverseAppBar, {
  AppBarPositionEnum,
} from '@components/MytaverseAppBar';

import { EventDrawerTabs } from '../../../constants';

import { IDashboardTopBarContentView } from './interfaces';

import { useStyles } from './styles';

import { useClientSpecificIsAdmin } from '@hooks/clientSpecificUtils';
import { useMytaverseEvent } from '@core/dashboard/providers';
import ConfirmModal from '@components/ConfirmModal';
import ConfirmLogoutModal, {
  useOpenConfirmLogoutModal,
} from '@components/ConfirmLogoutModal';
import { useChatState } from '@core/chat';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const {
  REACT_APP_SHOW_LEFT_BAR,
  REACT_APP_SHOW_PROFILE,
  REACT_APP_SHOW_ADMIN_TOOLS,
  REACT_APP_EXTERNAL_EVENT_TAG,
} = env;

const DashboardTopBarContentView = ({
  currentRoom,
  isOpenChat,
  hasMediaStream,
  participantTotal,
  onOpenEventDrawer,
  currentEvent,
}: IDashboardTopBarContentView) => {
  const classes = useStyles({
    isOpenChat,
    hasMediaStream,
  });
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { skyLinkExperience, events, cleanStates } = useMytaverseEvent();
  const { resetState } = useChatState();
  const [showExitSkyLinkModal, setShowExitSkyLinkModal] = useState(false);
  const {
    open: isOpenedConfirmLogoutModal,
    openModal: onOpenConfirmLogoutModal,
    closeModal: onCloseConfirmLogoutModal,
  } = useOpenConfirmLogoutModal();
  const showLeftMenu = currentEvent?.enablePureGamecast || currentRoom;
  const showAdminTools = REACT_APP_SHOW_ADMIN_TOOLS === 'true';
  const showLeftBar = REACT_APP_SHOW_LEFT_BAR === 'true';
  const showProfile =
    REACT_APP_SHOW_PROFILE === 'true' ||
    (REACT_APP_EXTERNAL_EVENT_TAG
      ? currentEvent?.eventTags?.includes(REACT_APP_EXTERNAL_EVENT_TAG)
      : false);

  //TODO: when it's active, it should be added to mobile as well
  const isAdmin = useClientSpecificIsAdmin();
  const isCurrentParticipantAdmin = showAdminTools && isAdmin;
  const allowOnMobileForExternalEvent =
    !!REACT_APP_EXTERNAL_EVENT_TAG &&
    currentEvent?.eventTags?.includes(REACT_APP_EXTERNAL_EVENT_TAG);

  const handleExitButtonClick = () => {
    const targetEvent = events.find((event) => {
      const tags = event.eventTags || [];
      return (
        tags.includes('SD') &&
        tags.includes('FullTimeSDHub') &&
        !tags.includes('LINUX')
      );
    });

    if (targetEvent) {
      const link = pathname.match(/\/events\//)
        ? pathname.replace(/(\/events\/)[^/]+/, `$1${targetEvent.eventId}`)
        : `/events/${targetEvent.eventId}`;

      localStorage.setItem('returnToSkyLinkPortal', 'true');

      window.open(link, '_self');
    } else {
      console.log(
        'No matching event found with SD, FullTimeSDHub tags (excluding LINUX)',
      );
    }
  };

  if (isMobileOnly && !allowOnMobileForExternalEvent) {
    return null;
  }

  return (
    <>
      {showLeftBar ? (
        <MytaverseAppBar
          position={AppBarPositionEnum.TopLeft}
          sx={classes.leftBar}
          isCollapsible={false}
          isGroup={false}
        >
          {showLeftMenu ? (
            <>
              <Box sx={classes.root} component="div">
                <DashboardBarButton
                  buttonIcon={<LocationOnIcon />}
                  onClick={() => onOpenEventDrawer(EventDrawerTabs.Locations)}
                >
                  {currentRoom?.name || 'Lobby'}
                </DashboardBarButton>
                <ParticipantsButton
                  onClick={() =>
                    onOpenEventDrawer(EventDrawerTabs.Participants)
                  }
                  total={participantTotal}
                />
              </Box>
              <IconButton
                sx={classes.iconButton}
                onClick={() => onOpenEventDrawer(EventDrawerTabs.Locations)}
              >
                <MenuIcon />
              </IconButton>
            </>
          ) : (
            <Box sx={classes.rootLoading}>
              <Stack flexDirection="row" alignItems="center">
                <AppLogo />
              </Stack>
            </Box>
          )}
        </MytaverseAppBar>
      ) : null}
      {skyLinkExperience ? (
        (() => {
          const params = new URLSearchParams(window.location.search);
          const isSkylinkTrue = params.get('skylink') === 'true';
          return isSkylinkTrue ? (
            <MytaverseAppBar
              position={AppBarPositionEnum.TopRight}
              isCollapsible={false}
            >
              <Box
                sx={classes.skyLinkReturnContainer}
                onClick={() => setShowExitSkyLinkModal(true)}
              >
                <LogoutIcon />
                <Typography sx={classes.skyLinkReturnLabel}>
                  {t('skyLink.returnToHub')}
                </Typography>
              </Box>
            </MytaverseAppBar>
          ) : (
            <MytaverseAppBar
              position={AppBarPositionEnum.TopRight}
              isCollapsible={false}
            >
              <Box
                sx={classes.skyLinkReturnContainer}
                onClick={onOpenConfirmLogoutModal}
              >
                <LogoutIcon />
                <Typography sx={classes.skyLinkReturnLabel}>
                  {t('common:logoutConfirm.exit')}
                </Typography>
              </Box>
            </MytaverseAppBar>
          );
        })()
      ) : (
        <MytaverseAppBar
          position={AppBarPositionEnum.TopRight}
          isCollapsible={false}
        >
          <FullScreenButton />
          {/*{showInviteUserBtn ? (*/}
          {/*  <DashboardBarButton*/}
          {/*    onClick={onOpenInviteUserModal}*/}
          {/*    buttonIcon={<PersonAddIcon sx={classes.addPersonIcon} />}*/}
          {/*    sx={classes.inviteUserBtn}*/}
          {/*  />*/}
          {/*) : null}*/}
          {isCurrentParticipantAdmin ? <AdminTools /> : null}
          {showProfile ? <UserAvatarButton /> : null}
        </MytaverseAppBar>
      )}

      {skyLinkExperience
        ? (() => {
            const params = new URLSearchParams(window.location.search);
            const isSkylinkTrue = params.get('skylink') === 'true';
            return isSkylinkTrue ? (
              <ConfirmModal
                open={showExitSkyLinkModal}
                title={t('skyLink.exitConfirmTitle')}
                message={t('skyLink.exitConfirmMessage')}
                onConfirm={handleExitButtonClick}
                onCancel={() => setShowExitSkyLinkModal(false)}
                cancelButtonName={t('common:logoutConfirm.stayHub')}
                confirmButtonName={t('common:logoutConfirm.exitHub')}
                altStyling
              />
            ) : (
              <ConfirmLogoutModal
                open={isOpenedConfirmLogoutModal}
                onClose={onCloseConfirmLogoutModal}
                cleanAndResetStates={[cleanStates, resetState]}
                altStyling
              />
            );
          })()
        : null}
    </>
  );
};

export default DashboardTopBarContentView;
